import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faMoon, faSnowflake, faSun, faCloud, faCloudBolt, faCloudMeatball, faCloudMoon, faCloudMoonRain, faCloudRain, faCloudShowersHeavy, faCloudShowersWater, faCloudSun, faCloudSunRain, faSmog } from "@fortawesome/free-solid-svg-icons";

export interface Weather {
  latitude: number,
  longitude: number,
  timezone_abbreviation: string,
  current_weather: CurrentWeather,
  hourly_units: HourlyWeatherUnitRaw,
  hourly: {
    time: string[],
    temperature_2m_gfs_global: number[],
    apparent_temperature_gfs_global: number[],
    relativehumidity_2m_gfs_global: number[],
    precipitation_probability_gfs_global: number[],
    rain_gfs_global: number[],
    showers_gfs_global: number[],
    snowfall_gfs_global: number[],
    weathercode_ecmwf_ifs04: number[],
    visibility_gfs_global: number[],
    is_day_gfs_global: number[],
  },
  daily_units: DailyWeatherUnitRaw,
  daily: {
    time: string[],
    weathercode_ecmwf_ifs04: number[],
    temperature_2m_max_gfs_global: number[],
    temperature_2m_min_gfs_global: number[],
    uv_index_max_gfs_global: number[],
    precipitation_probability_mean_gfs_global: number[],
    sunrise: string[],
    sunset: string[],
  },
}

export interface HourlyWeatherUnitRaw {
  temperature_2m_gfs_global: string,
  relativehumidity_2m_gfs_global: string,
  apparent_temperature_gfs_global: string,
  precipitation_probability_gfs_global: string,
  rain_gfs_global: string,
  showers_gfs_global: string,
  snowfall_gfs_global: string,
  visibility_gfs_global: string,
  is_day_gfs_global: number,
};

export interface DailyWeatherUnitRaw {
  temperature_2m_max_gfs_global: string,
  temperature_2m_min_gfs_global: string,
  uv_index_max_gfs_global: string,
  precipitation_probability_mean_gfs_global: string,
  sunrise: string,
  sunset: string,
};

export interface WeatherAlertMsg {
  text: string,
}

export interface WeatherAlert {
  name: string,
  areaName: string,
  description: string,
  issuedTime: string,
  effectiveTime: string,
  expireTime: string,
  source: string,
  severity: string,
  timezone: string,
  messages: WeatherAlertMsg[],
}

export interface AirQualityIndexUnit {
  time: string,
  us_aqi: string,
}

export interface AirQualityIndex {
  latitude: number,
  longitude: number,
  hourly_units: AirQualityIndexUnit,
  timezone: string,
  hourly: {
    time: string[],
    us_aqi: (number | null)[],
  }
}


export type DegreeType = 'celsius' | 'fahrenheit';

export const DAY_NIGHT_TEXT_COLOR = 'whitesmoke';
export const SECONDARY_GRAY_COLOR = '#B6B6B4';
export const DEFAULT_BACKGROUND_COLOR = '#29465B';

export interface WeatherCodeInfo {
  text: string,
  dayIcon: IconDefinition,
  nightIcon: IconDefinition,
  dayColor: string,
  nightColor: string,
  dayBgColor: string,
  nightBgColor: string,
}

export const weatherCodes = new Map<number, WeatherCodeInfo>(
  [
    [0, {
      text: "Clear sky",
      dayIcon: faSun, nightIcon: faMoon,
      dayColor: '#2554C7', nightColor: '#414953',
      dayBgColor: 'linear-gradient(135deg, #2554C7, #E86507)', nightBgColor: 'linear-gradient(135deg, #414953, #6A3F1A)',
    }],
    [1, {
      text: "Mainly clear",
      dayIcon: faCloudSun, nightIcon: faCloudMoon,
      dayColor: '#2554C7', nightColor: '#414953',
      dayBgColor: 'linear-gradient(135deg, #2554C7, #E86507)', nightBgColor: 'linear-gradient(135deg, #414953, #6A3F1A)',
    }],
    [2, {
      text: "Partly cloudy",
      dayIcon: faCloudSun, nightIcon: faCloudMoon,
      dayColor: '#0041C2', nightColor: '#414953',
      dayBgColor: 'linear-gradient(135deg, #0041C2, #E36602)', nightBgColor: 'linear-gradient(135deg, #414953, #6A3F1A)',
    }],
    [3, {
      text: "Overcast",
      dayIcon: faCloud, nightIcon: faCloud,
      dayColor: '#4E6086', nightColor: '#282825',
      dayBgColor: 'linear-gradient(135deg, #4E6086, #A55A1F)', nightBgColor: 'linear-gradient(135deg, #282825, #41240B)',
    }],
    [45, {
      text: "Fog",
      dayIcon: faSmog, nightIcon: faSmog,
      dayColor: '#5E6784', nightColor: '#282825',
      dayBgColor: 'linear-gradient(135deg, #5E6784, #A2622A)', nightBgColor: 'linear-gradient(135deg, #282825, #41240B)',
    }],
    [48, {
      text: "Fog",
      dayIcon: faSmog, nightIcon: faSmog,
      dayColor: '#5E6784', nightColor: '#282825',
      dayBgColor: 'linear-gradient(135deg, #5E6784, #A2622A)', nightBgColor: 'linear-gradient(135deg, #282825, #41240B)',
    }],
    [51, {
      text: "Light drizzle",
      dayIcon: faCloudSunRain, nightIcon: faCloudMoonRain,
      dayColor: '#4863A0', nightColor: '#201931',
      dayBgColor: 'linear-gradient(135deg, #4863A0, #C36119)', nightBgColor: 'linear-gradient(135deg, #201931, #472806)',
    }],
    [53, {
      text: "Moderate drizzle",
      dayIcon: faCloudSunRain, nightIcon: faCloudMoonRain,
      dayColor: '#4863A0', nightColor: '#201931',
      dayBgColor: 'linear-gradient(135deg, #4863A0, #C36119)', nightBgColor: 'linear-gradient(135deg, #201931, #472806)',
    }],
    [55, {
      text: "Dense drizzle",
      dayIcon: faCloudSunRain, nightIcon: faCloudMoonRain,
      dayColor: '#4863A0', nightColor: '#201931',
      dayBgColor: 'linear-gradient(135deg, #4863A0, #C36119)', nightBgColor: 'linear-gradient(135deg, #201931, #472806)',
    }],
    [56, {
      text: "Light freezing Drizzle",
      dayIcon: faCloudSunRain, nightIcon: faCloudMoonRain,
      dayColor: '#4863A0', nightColor: '#201931',
      dayBgColor: 'linear-gradient(135deg, #4863A0, #C36119)', nightBgColor: 'linear-gradient(135deg, #201931, #472806)',
    }],
    [57, {
      text: "Dense freezing Drizzle",
      dayIcon: faCloudSunRain, nightIcon: faCloudMoonRain,
      dayColor: '#4863A0', nightColor: '#201931',
      dayBgColor: 'linear-gradient(135deg, #4863A0, #C36119)', nightBgColor: 'linear-gradient(135deg, #201931, #472806)',
    }],
    [61, {
      text: "Light rain",
      dayIcon: faCloudRain, nightIcon: faCloudRain,
      dayColor: '#594968', nightColor: '#0E171E',
      dayBgColor: 'linear-gradient(135deg, #594968, #7D591E)', nightBgColor: 'linear-gradient(135deg, #0E171E, #3C1D03)',
    }],
    [63, {
      text: "Moderate rain",
      dayIcon: faCloudRain, nightIcon: faCloudRain,
      dayColor: '#594968', nightColor: '#0E171E',
      dayBgColor: 'linear-gradient(135deg, #594968, #7D591E)', nightBgColor: 'linear-gradient(135deg, #0E171E, #3C1D03)',
    }],
    [65, {
      text: "Heavy rain",
      dayIcon: faCloudShowersHeavy, nightIcon: faCloudShowersHeavy,
      dayColor: '#3F225A', nightColor: '#0E171E',
      dayBgColor: 'linear-gradient(135deg, #3F225A, #6B490B)', nightBgColor: 'linear-gradient(135deg, #0E171E, #3C1D03)',
    }],
    [66, {
      text: "Light freezing rain",
      dayIcon: faCloudRain, nightIcon: faCloudRain,
      dayColor: '#42285A', nightColor: '#1F1D1D',
      dayBgColor: 'linear-gradient(135deg, #42285A, #6B4A0D)', nightBgColor: 'linear-gradient(135deg, #1F1D1D, #3B2107)',
    }],
    [67, {
      text: "Heavy freezing rain",
      dayIcon: faCloudShowersHeavy, nightIcon: faCloudShowersHeavy,
      dayColor: '#3F225A', nightColor: '#1F1D1D',
      dayBgColor: 'linear-gradient(135deg, #3F225A, #6B490B)', nightBgColor: 'linear-gradient(135deg, #1F1D1D, #3B2107)',
    }],
    [71, {
      text: "Light snow fall",
      dayIcon: faSnowflake, nightIcon: faSnowflake,
      dayColor: '#913A65', nightColor: '#504A4B',
      dayBgColor: 'linear-gradient(135deg, #913A65, #6B8925)', nightBgColor: 'linear-gradient(135deg, #504A4B, #604520)',
    }],
    [73, {
      text: "Moderate snow fall",
      dayIcon: faSnowflake, nightIcon: faSnowflake,
      dayColor: '#913A65', nightColor: '#504A4B',
      dayBgColor: 'linear-gradient(135deg, #913A65, #6B8925)', nightBgColor: 'linear-gradient(135deg, #504A4B, #604520)',
    }],
    [75, {
      text: "Heavy snow fall",
      dayIcon: faSnowflake, nightIcon: faSnowflake,
      dayColor: '#913A65', nightColor: '#504A4B',
      dayBgColor: 'linear-gradient(135deg, #913A65, #6B8925)', nightBgColor: 'linear-gradient(135deg, #504A4B, #604520)',
    }],
    [77, {
      text: "Snow grains",
      dayIcon: faSnowflake, nightIcon: faSnowflake,
      dayColor: '#913A65', nightColor: '#504A4B',
      dayBgColor: 'linear-gradient(135deg, #913A65, #6B8925)', nightBgColor: 'linear-gradient(135deg, #504A4B, #604520)',
    }],
    [80, {
      text: "Light rain showers",
      dayIcon: faCloudShowersHeavy, nightIcon: faCloudShowersHeavy,
      dayColor: '#1F45FC', nightColor: '#2C202C',
      dayBgColor: 'linear-gradient(135deg, #1F45FC, #FF7202)', nightBgColor: 'linear-gradient(135deg, #2C202C, #432A09)',
    }],
    [81, {
      text: "Moderate rain showers",
      dayIcon: faCloudShowersHeavy, nightIcon: faCloudShowersHeavy,
      dayColor: '#1F45FC', nightColor: '#2C202C',
      dayBgColor: 'linear-gradient(135deg, #1F45FC, #FF7202)', nightBgColor: 'linear-gradient(135deg, #2C202C, #432A09)',
    }],
    [82, {
      text: "Heavy rain showers",
      dayIcon: faCloudShowersHeavy, nightIcon: faCloudShowersHeavy,
      dayColor: '#36454F', nightColor: '#2C202C',
      dayBgColor: 'linear-gradient(135deg, #36454F, #663915)', nightBgColor: 'linear-gradient(135deg, #2C202C, #432A09)',
    }],
    [85, {
      text: "Light snow showers",
      dayIcon: faCloudShowersWater, nightIcon: faCloudShowersWater,
      dayColor: '#1F45FC', nightColor: '#2C202C',
      dayBgColor: 'linear-gradient(135deg, #1F45FC, #FF7202)', nightBgColor: 'linear-gradient(135deg, #2C202C, #432A09)',
    }],
    [86, {
      text: "Heavy snow showers",
      dayIcon: faCloudShowersWater, nightIcon: faCloudShowersWater,
      dayColor: '#1F45FC', nightColor: '#2C202C',
      dayBgColor: 'linear-gradient(135deg, #1F45FC, #FF7202)', nightBgColor: 'linear-gradient(135deg, #2C202C, #432A09)',
    }],
    [95, {
      text: "Thunderstorm",
      dayIcon: faCloudBolt, nightIcon: faCloudBolt,
      dayColor: '#554C52', nightColor: '#0C0F19',
      dayBgColor: 'linear-gradient(135deg, #554C52, #664B21)', nightBgColor: 'linear-gradient(135deg, #0C0F19, #391C02)',
    }],
    [96, {
      text: "Light thunderstorm hail",
      dayIcon: faCloudMeatball, nightIcon: faCloudMeatball,
      dayColor: '#554C52', nightColor: '#0C0F19',
      dayBgColor: 'linear-gradient(135deg, #554C52, #664B21)', nightBgColor: 'linear-gradient(135deg, #0C0F19, #391C02)',
    }],
    [99, {
      text: "Heavy thunderstorm hail",
      dayIcon: faCloudMeatball, nightIcon: faCloudMeatball,
      dayColor: '#554C52', nightColor: '#0C0F19',
      dayBgColor: 'linear-gradient(135deg, #554C52, #664B21)', nightBgColor: 'linear-gradient(135deg, #0C0F19, #391C02)',
    }],
  ]
);

export const uvIndexCodes = (index: number): string => {
  switch (index) {
    case 0:
    case 1:
    case 2:
      return "Low";
    case 3:
    case 4:
    case 5:
      return "Moderate";
    case 6:
    case 7:
      return "High";
    case 8:
    case 9:
    case 10:
      return "Very High";
    default:
      return "Extreme";
  }
};

export const airQualityIndexCodes = (aqi: number): string => {
  if (aqi >= 0 && aqi <= 50) return `Good`
  else if (aqi >= 51 && aqi <= 100) return `Moderate`
  else if (aqi >= 101 && aqi <= 150) return `Unhealthy for sensitive groups`
  else if (aqi >= 151 && aqi <= 200) return `Unhealthy`
  else if (aqi >= 201 && aqi <= 300) return `Very unhealthy`
  else return `Hazardous`
}


export interface HourlyWeather {
  time: string,
  temperature_2m: number,
  apparent_temperature: number,
  relativehumidity_2m: number,
  precipitation_probability: number,
  rain: number,
  showers: number,
  snowfall: number,
  weathercode: number,
  visibility: number,
  is_day: number,
};

export interface DailyWeather {
  time: string,
  weathercode: number,
  temperature_2m_max: number,
  temperature_2m_min: number,
  uv_index_max: number,
  precipitation_probability_mean: number,
  sunrise: string,
  sunset: string,
};


export interface HourlyWeatherUnit {
  temperature_2m: string,
  relativehumidity_2m: string,
  apparent_temperature: string,
  precipitation_probability: string,
  rain: string,
  showers: string,
  snowfall: string,
  visibility: string,
  is_day: number,
};

export interface DailyWeatherUnit {
  temperature_2m_max: string,
  temperature_2m_min: string,
  uv_index_max: string,
  precipitation_probability_mean: string,
  sunrise: string,
  sunset: string,
};

export interface CurrentWeather {
  temperature: number,
  windspeed: number,
  winddirection: number,
  weathercode: number,
  is_day: number,
  time: string,
};

export interface HourlyAirQualityIndex {
  time: string,
  timezone: string,
  us_aqi: number | null,
}

